import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Calendar, Download, RefreshCw } from 'lucide-react';

interface CalendarData {
  round_number: { [key: string]: string };
  event_name: { [key: string]: string };
  location: { [key: string]: string };
  country: { [key: string]: string };
  event_date: { [key: string]: string };
  event_format: { [key: string]: string };
}

// const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

const F1CalendarGenerator = () => {
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [remoteUrl, setRemoteUrl] = useState('');
  const [options, setOptions] = useState({
    includePractice: true,
    includeQualifying: true,
    includeRace: true,
    includeSprint: true,
  });

  useEffect(() => {
    loadLocalData();
  }, []);

  const loadLocalData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/calendar');
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText.slice(0, 200)}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error(`Expected JSON but got ${contentType}`);
      }
      
      const data = await response.json();
      setCalendarData(data);
      setLoading(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      console.error('Full error:', error);
      setError('Error loading calendar data: ' + errorMessage);
      setLoading(false);
    }
  };

  const loadRemoteData = async (url: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/remote-calendar?url=${encodeURIComponent(url)}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCalendarData(data);
      setLoading(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setError('Error loading remote calendar data: ' + errorMessage);
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (remoteUrl) {
      await loadRemoteData(remoteUrl);
    } else {
      await loadLocalData();
    }
  };

  const formatDisplayDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getCalendarUrl = (type: 'download' | 'google' | 'webcal' = 'download') => {
    const queryParams = new URLSearchParams({
      practice: options.includePractice.toString(),
      qualifying: options.includeQualifying.toString(),
      race: options.includeRace.toString(),
      sprint: options.includeSprint.toString()
    }).toString();
  
    const calendarPath = `/calendar.ics?${queryParams}`;
    
    if (type === 'google') {
      return `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(
        `webcal://${window.location.host}${calendarPath}`
      )}`;
    }
    
    if (type === 'webcal') {
      return `webcal://${window.location.host}${calendarPath}`;
    }
    
    return calendarPath;
  };

  const downloadCalendar = () => {
    window.location.href = getCalendarUrl('download');
  };

  if (loading) {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="py-6">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-6 h-6 animate-spin" />
            <span className="ml-2">Loading calendar data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-6 h-6" />
          Formula 1 2026 Calendar Generator
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Data Source Section */}
          <div className="space-y-4">
            <h3 className="font-medium">Data Source:</h3>
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="Enter remote JSON URL (optional)"
                value={remoteUrl}
                onChange={(e) => setRemoteUrl(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={handleRefresh}
                disabled={loading}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>

          {/* Options Section */}
          <div className="space-y-4">
            <h3 className="font-medium">Include Events:</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="practice" 
                  checked={options.includePractice}
                  onCheckedChange={(checked) => 
                    setOptions(prev => ({ ...prev, includePractice: checked === true }))}
                />
                <label htmlFor="practice">Practice Sessions</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="qualifying" 
                  checked={options.includeQualifying}
                  onCheckedChange={(checked) => 
                    setOptions(prev => ({ ...prev, includeQualifying: checked === true }))}
                />
                <label htmlFor="qualifying">Qualifying</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="sprint" 
                  checked={options.includeSprint}
                  onCheckedChange={(checked) => 
                    setOptions(prev => ({ ...prev, includeSprint: checked === true }))}
                />
                <label htmlFor="sprint">Sprint Races</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="race" 
                  checked={options.includeRace}
                  onCheckedChange={(checked) => 
                    setOptions(prev => ({ ...prev, includeRace: checked === true }))}
                />
                <label htmlFor="race">Main Races</label>
              </div>
            </div>
          </div>

          {/* Schedule Preview Section */}
          {calendarData && (
            <div className="space-y-4">
              <h3 className="font-medium">Schedule Preview</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto rounded-lg border">
                {Object.keys(calendarData.round_number)
                  .filter(round => round !== "0") // Skip testing round
                  .map(round => {
                    const roundNum = calendarData.round_number[round];
                    return (
                      <div 
                        key={round}
                        className="p-4 hover:bg-gray-50 border-b last:border-b-0"
                      >
                        <div className="font-medium">
                          Round {roundNum}: {calendarData.event_name[round]}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {calendarData.location[round]}, {calendarData.country[round]}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {formatDisplayDate(calendarData.event_date[round])}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Format: {calendarData.event_format[round].replace('_', ' ')}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Calendar Links Section */}
          {calendarData && (
            <>
              <div className="space-y-4">
                <h3 className="font-medium">Calendar Subscription Links:</h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Google Calendar:</p>
                    <code className="block p-2 bg-gray-100 rounded text-sm break-all">
                      {getCalendarUrl('google')}
                    </code>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">iCloud/Outlook Calendar:</p>
                    <code className="block p-2 bg-gray-100 rounded text-sm break-all">
                      {getCalendarUrl('webcal')}
                    </code>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Direct Link (for testing):</p>
                    <code className="block p-2 bg-gray-100 rounded text-sm break-all">
                      <a href={getCalendarUrl('download')} className="text-blue-600 hover:underline">
                        {getCalendarUrl('download')}
                      </a>
                    </code>
                  </div>
                </div>
              </div>
          
              <Button 
                onClick={downloadCalendar}
                className="w-full flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download Calendar (.ics)
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default F1CalendarGenerator;